/**
 * GroupsTableRow - Linha da tabela de grupos
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  Checkbox,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import LinkIcon from '@mui/icons-material/Link';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import CollectionActions from './CollectionActions';

const GroupsTableRow = memo(({
  row,
  onRefresh,
  onNavigate,
  parentName,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Cor do avatar baseada se tem parent ou não - USA TOKENS DINÂMICOS
  const isSubgroup = Boolean(row.groupId);
  const avatarColor = isSubgroup
    ? tokens?.semantic?.info
    : tokens?.semantic?.success;

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 0.5, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 0.5, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarColor}20`,
            border: `1px solid ${avatarColor}40`,
          }}
        >
          <FolderIcon sx={{ fontSize: 18, color: avatarColor }} />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 0.5, width: 70 }}>
        <Typography variant="caption" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 250 }}>
            {row.name}
          </Typography>
          {isSubgroup && (
            <Tooltip title={`${t('groupParent')}: ${parentName || row.groupId}`}>
              <SubdirectoryArrowRightIcon sx={{ fontSize: 14, color: tokens?.text?.tertiary }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>

      {/* Parent Group */}
      <TableCell sx={{ py: 0.5 }}>
        {parentName ? (
          <Chip
            size="small"
            label={parentName}
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 500,
              bgcolor: tokens?.semantic?.infoBg,
              color: tokens?.semantic?.info,
              border: `1px solid ${tokens?.semantic?.info}30`,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: tokens?.text?.tertiary }}>
            —
          </Typography>
        )}
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 0.5 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/group"
          endpoint="groups"
          setTimestamp={onRefresh}
          customActions={[
            {
              key: 'connections',
              title: t('sharedConnections'),
              icon: <LinkIcon />,
              variant: 'warning',
              handler: (id) => onNavigate(`/settings/group/${id}/connections`),
            },
          ]}
        />
      </TableCell>
    </TableRow>
  );
});

GroupsTableRow.displayName = 'GroupsTableRow';

export default GroupsTableRow;
